import { useEffect, useRef } from 'react';
import { pusher } from '../../pusher';

/**
 * Subscribes to the organisation Pusher channel and runs the supplied refetch
 * whenever any project in the org has finished a long-running task (convert, validate).
 *
 * The server emits `project-updated` on `organisation-{orgId}` after convert/validate
 * workers finish. One channel per page, regardless of project count.
 *
 * The callback is held in a ref so consumers can pass an inline closure without forcing
 * a resubscribe on every render — that churn previously dropped events whenever the parent
 * re-rendered (e.g. when a refetch updated cached data).
 */
export const useOrganisationProjectEvents = ({ organisationId, onProjectUpdated }: { organisationId: string; onProjectUpdated: () => void }) => {
  const callbackRef = useRef(onProjectUpdated);
  useEffect(() => {
    callbackRef.current = onProjectUpdated;
  }, [onProjectUpdated]);

  useEffect(() => {
    if (!organisationId) return;
    const channelName = `organisation-${organisationId}`;
    const channel = pusher.subscribe(channelName);
    const handler = () => callbackRef.current();
    channel.bind('project-updated', handler);
    return () => {
      channel.unbind('project-updated', handler);
      channel.unsubscribe();
    };
  }, [organisationId]);
};
